import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { initialize, Field, reduxForm } from "redux-form";
import { Button, ReduxControls } from "rhombus-react";

import { closeModal } from 'reducers/Modal';
import { assignContentFeed } from "reducers/AssignContentFeed";
import { isEmail, isURL, required } from "forms/validators/validators";
import { noop } from "utils/common";

const ASSIGN_CONTENT_FEEDS_FORM = "assign-content-feeds-form";

import ProgressContainer from "widgets/ProgressContainer";
import BasicModal from "widgets/modals/BasicModal";

import { loadCreatives } from "reducers/Creatives";

import "./AssignContentFeed.css";

const emailFieldValidator = (value) => {
	if (isEmail(value)) {
		return undefined;
	}
	return "Please enter a valid email address";
};

const GOOGLE_SPREADSHEET_SOURCE_TYPE = "Google Spreadsheet";

const SOURCE_TYPE_OPTIONS = [{
	label: GOOGLE_SPREADSHEET_SOURCE_TYPE,
	value: GOOGLE_SPREADSHEET_SOURCE_TYPE,
}];

const emailTooltip = {
	inline: false,
	direction: "right",
	size: "large",
	iconSize: "small",
	description: "Email for status updates",
};

function AssignContentFeed(props) {
	const dispatch = useDispatch();
	const {
		creativeId,
		creativeIds,
		creativeName,
		creatives,
		handleSubmit,
		submitting,
		invalid,
		user,
		libraryId,
	} = props;

	const onClose = () => dispatch(closeModal());

	const targetCreativeIds = creativeIds || (creatives ? creatives.map(cr => cr.id) : [creativeId]);

	useEffect(() => {
		dispatch(initialize(ASSIGN_CONTENT_FEEDS_FORM, {
			creativeId: targetCreativeIds[0],
			creativeIds: targetCreativeIds,
			email: user.email,
			sourceType: GOOGLE_SPREADSHEET_SOURCE_TYPE,
			libraryId,
		}));
	}, []);

	const renderContent = () => (
		<ProgressContainer isLoading={submitting}>
			<form className="assign-content-feed-form" onSubmit={handleSubmit(submitFunction)}
				  id={ASSIGN_CONTENT_FEEDS_FORM}>
				<div className={"assign-content-feed-form-field"}>
					<Field
						name="sourceUrl"
						label="Source URL"
						component={ReduxControls.Input}
						autoFocus
						required
						validate={[isURL, required]}
					/>
				</div>
				<div className={"assign-content-feed-form-field"}>
					<Field
						name="sourceType"
						label="Source Type"
						component={ReduxControls.Select}
						options={SOURCE_TYPE_OPTIONS}
						isClearable={false}
						normalize={value => value?.value}
						format={value => ({ label: value, value })}
						required
						onMenuScrollToBottom={noop}
					/>
				</div>
				<div className={"assign-content-feed-form-field assign-content-feed-form-last-field"}>
					<Field
						name="email"
						label="Email"
						component={ReduxControls.Input}
						required
						validate={[emailFieldValidator]}
						tip={emailTooltip}
					/>
				</div>
			</form>
		</ProgressContainer>
	);

	const renderFooter = () => ([
		<Button key="cancel" theme="grey" disabled={submitting} onClick={onClose}>Cancel</Button>,
		<Button
			key="submit"
			theme="blue"
			disabled={submitting || invalid}
			type="submit"
			form={ASSIGN_CONTENT_FEEDS_FORM}
		>
			Assign Bulk Sheet
		</Button>
	]);

	const modalProps = {
		title: `Assign Bulk Sheet - ${creativeName}`,
		onClose,
		renderContent,
		renderFooter,
	};

	return <BasicModal {...modalProps} />;
}

AssignContentFeed.displayName = "AssignContentFeed";

function submitFunction(values, dispatch) {
	const { sourceUrl, sourceType, creativeId, creativeIds, email, libraryId } = values;
	const targetCreativeIds = creativeIds || (creativeId ? [creativeId] : []);
	return dispatch(assignContentFeed(sourceUrl, sourceType, targetCreativeIds, email, libraryId))
		.then((resp) => {
			if (resp.success) {
				dispatch(loadCreatives(libraryId));
				return dispatch(closeModal());
			}
		});
}

export default reduxForm({
	form: ASSIGN_CONTENT_FEEDS_FORM,
	onSubmit: submitFunction,
})(AssignContentFeed);
